import * as stylex from '@stylexjs/stylex';

// Sentinel design tokens.
//
// **Why we mirror miraidon's OKLCH values rather than re-export them**
// Miraidon ships its design tokens as pre-compiled `var(--miraidon-…)`
// strings. The StyleX babel plugin requires `defineVars` calls to be
// statically analyzable from a `.stylex.ts` source file — it can't trace
// through miraidon's compiled chunks, so importing `colors` from
// `@VicOne-RD/miraidon-ui/tokens` and using it in a `stylex.create`
// call fails the build with:
//   "Could not resolve the path to the imported file. Please ensure
//    that the theme file has a .stylex.js or .stylex.ts extension."
//
// To work around this we define our own sentinel-owned VarGroup here,
// using the same OKLCH source values as miraidon. Both systems remain
// visually identical (miraidon styles its components, sentinel styles
// its composites), and StyleX can statically analyze every reference.
//
// Source values are pinned to:
//   ~/github/ref/vicone-miraidon/packages/ui/src/tokens/colors.stylex.ts
// Re-sync if miraidon ever revs its palette.
//
// **How to import this from an app**
// The plugin resolves a token module by import path, and it cannot follow the
// `@sentinel/ui` barrel. That is the only barrier, and it applies only inside
// `stylex.create`:
//
//   inside this package            -> `from '../theme/semantic.stylex'`
//   an app file, in stylex.create  -> `from '@sentinel/ui/theme/semantic.stylex'`
//   an app file, inline style only -> either; the barrel is fine
//
// Twelve app files used to carry a comment concluding that app code must inline
// the OKLCH values because the plugin "cannot trace the VarGroup across the
// package boundary". The observation was right and the conclusion was not — a
// subpath export works, including inside a `:hover` block. Both directions were
// checked with a real `vite build`; a typecheck does not run the plugin and
// proves nothing here.
//
// **Deriving an alpha variant**
// The palette deliberately has no token for every alpha of every colour. Write
// `color-mix(in oklab, ${sentinel.x} 60%, transparent)` instead of a new literal,
// so the value follows `x` when the palette is retuned. Use `in oklab`, not
// `in oklch`: these values compile to `lab()`, and mixing a near-neutral lab
// colour in oklch yields a powerless hue that renders a few 1/255 off.

export const sentinel = stylex.defineVars({
  // ── Surfaces (bice neutrals — dark security-product feel) ─────────
  surfaceApp: 'oklch(18.24% 0.0152 266.74)', // bice.90 = miraidon bgPrimary
  surfaceElevated: 'oklch(22.71% 0.0204 267.76)', // bice.80 = miraidon bgSecondary
  surfaceOverlay: 'oklch(27.41% 0.0230 270.44)', // bice.70 = miraidon bgTertiary
  surfaceSunken: 'oklch(13.92% 0.0097 261.76)', // bice.100 — deepest
  surfaceInverse: 'oklch(96.05% 0.0046 258.32)', // bice.10 = miraidon bgInverse

  // ── Text ──────────────────────────────────────────────────────────
  textBody: 'oklch(96.05% 0.0046 258.32 / 0.92)',
  textMuted: 'oklch(96.05% 0.0046 258.32 / 0.6)',
  textMutedStrong: 'oklch(96.05% 0.0046 258.32 / 0.7)',
  textSubtle: 'oklch(96.05% 0.0046 258.32 / 0.47)',
  textDisabled: 'oklch(96.05% 0.0046 258.32 / 0.28)',
  textOnAccent: 'oklch(100% 0 0)',
  textLink: 'oklch(69.68% 0.1400 263.28)', // blue.40
  textError: 'oklch(65.13% 0.2378 16.23)', // red.50 = miraidon textError
  textWarning: 'oklch(71.93% 0.1842 43.69)', // orange.50

  // ── Borders ──────────────────────────────────────────────────────
  borderDefault: 'oklch(96.05% 0.0046 258.32 / 0.08)',
  borderEmphasis: 'oklch(96.05% 0.0046 258.32 / 0.16)',
  borderStrong: 'oklch(96.05% 0.0046 258.32 / 0.32)',
  borderFocus: 'oklch(69.68% 0.1400 263.28)', // blue.40 — same as link

  // ── State (interactive surfaces) ─────────────────────────────────
  stateHover: 'oklch(96.05% 0.0046 258.32 / 0.12)',
  stateSelect: 'oklch(96.05% 0.0046 258.32 / 0.08)',
  stateActive: 'oklch(96.05% 0.0046 258.32 / 0.06)',

  // ── Brand (tenant-overridable; defaults to VicOne purple) ─────────
  brandPrimary: 'oklch(47.34% 0.1717 353.56)',
  brandPrimaryEmphasis: 'oklch(56.90% 0.2153 359.08)', // pink.60
  brandPrimaryMuted: 'oklch(47.34% 0.1717 353.56 / 0.16)',

  // ── Action accent (non-tenant; static link/button affordance) ─────
  accentAction: 'oklch(69.68% 0.1400 263.28)', // blue.40
  accentActionEmphasis: 'oklch(64.64% 0.1610 262.58)', // blue.50

  // ── Risk semantic colours (for audit, dashboards, status pills) ──
  // WCAG AA contrast against textOnAccent (#fff) requires L ≤ ~0.18 luminance.
  // Tokens here are DARKER than miraidon upstream — divergence is intentional
  // for accessibility. See docs/superpowers/specs/2026-05-15-e2e-flaky-skip-elimination-design.md.
  riskCritical: 'oklch(54.65% 0.2124 1.06)', // pink.70 — already passes
  riskHigh: 'oklch(50% 0.22 16.23)', // red.70 (darkened from red.50)
  riskMedium: 'oklch(50% 0.16 43.69)', // orange.70 (darkened from orange.50)
  riskLow: 'oklch(52% 0.14 81.73)', // yellow.70 (darkened from yellow.50 — yellow gamut issue)
  riskSafe: 'oklch(50% 0.14 145.76)', // green.70 (darkened from green.50)
  riskInfo: 'oklch(50% 0.16 262.58)', // blue.70 (darkened from blue.50)
  riskUnknown: 'oklch(49.12% 0.0487 268.44)', // bice.50 — already passes

  // ── Warn badge (blocked-option pill in SearchableSelect) ─────────
  // Distinct from riskLow: this is the brighter, NON-AA-darkened yellow used
  // only for the small uppercase pill (e.g. "Super Admin" on a blocked option).
  warnBadgeText: 'oklch(85% 0.12 85)',
  warnBadgeSurface: 'oklch(85% 0.12 85 / 0.12)',
  warnBadgeBorder: 'oklch(85% 0.12 85 / 0.28)',

  // ── Motion ───────────────────────────────────────────────────────
  motionFast: '120ms',
  motionBase: '200ms',
  motionSlow: '320ms',
  easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
  easeInOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',

  // ── Typography (font family stacks aligned with miraidon) ────────
  fontBody:
    '"Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontDisplay:
    'Interstate-Light, "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontMono:
    '"Segoe UI Mono", "SFMono-Medium", "SF Mono", Menlo, Consolas, Courier, monospace',

  // ── Shadow tiers (tuned for dark bice surfaces) ──────────────────
  shadowThin: '0 2px 8px 0 rgba(7,9,13,.48), 0 1px 2px 0 rgba(7,9,13,.16)',
  shadowMedium: '0 4px 16px 0 rgba(0,0,0,.48), 0 2px 4px 0 rgba(0,0,0,.16)',
  shadowThick: '0 8px 32px 0 rgba(0,0,0,.48), 0 4px 8px 0 rgba(0,0,0,.16)',
  shadowGlow: '0 0 0 1px oklch(47.34% 0.1717 353.56 / 0.32)',
});

// Theme variant that wires brand colors to runtime CSS variables fed by
// BrandingProvider. Apply this theme on a wrapper element when tenant
// branding is active. Non-brand tokens are passed through unchanged so
// the override map can stay non-empty (StyleX requires it).
export const tenantBrand = stylex.createTheme(sentinel, {
  surfaceApp: 'oklch(18.24% 0.0152 266.74)',
  surfaceElevated: 'oklch(22.71% 0.0204 267.76)',
  surfaceOverlay: 'oklch(27.41% 0.0230 270.44)',
  surfaceSunken: 'oklch(13.92% 0.0097 261.76)',
  surfaceInverse: 'oklch(96.05% 0.0046 258.32)',
  textBody: 'oklch(96.05% 0.0046 258.32 / 0.92)',
  textMuted: 'oklch(96.05% 0.0046 258.32 / 0.6)',
  textMutedStrong: 'oklch(96.05% 0.0046 258.32 / 0.7)',
  textSubtle: 'oklch(96.05% 0.0046 258.32 / 0.47)',
  textDisabled: 'oklch(96.05% 0.0046 258.32 / 0.28)',
  textOnAccent: 'oklch(100% 0 0)',
  textLink: 'oklch(69.68% 0.1400 263.28)',
  textError: 'oklch(65.13% 0.2378 16.23)',
  textWarning: 'oklch(71.93% 0.1842 43.69)',
  borderDefault: 'oklch(96.05% 0.0046 258.32 / 0.08)',
  borderEmphasis: 'oklch(96.05% 0.0046 258.32 / 0.16)',
  borderStrong: 'oklch(96.05% 0.0046 258.32 / 0.32)',
  borderFocus: 'oklch(69.68% 0.1400 263.28)',
  stateHover: 'oklch(96.05% 0.0046 258.32 / 0.12)',
  stateSelect: 'oklch(96.05% 0.0046 258.32 / 0.08)',
  stateActive: 'oklch(96.05% 0.0046 258.32 / 0.06)',
  brandPrimary: 'var(--sentinel-brand-primary, oklch(47.34% 0.1717 353.56))',
  brandPrimaryEmphasis:
    'var(--sentinel-brand-primary-emphasis, oklch(56.90% 0.2153 359.08))',
  brandPrimaryMuted:
    'var(--sentinel-brand-primary-muted, oklch(47.34% 0.1717 353.56 / 0.16))',
  accentAction: 'oklch(69.68% 0.1400 263.28)',
  accentActionEmphasis: 'oklch(64.64% 0.1610 262.58)',
  riskCritical: 'oklch(54.65% 0.2124 1.06)', // pink.70 — already passes
  riskHigh: 'oklch(50% 0.22 16.23)', // red.70 (darkened from red.50)
  riskMedium: 'oklch(50% 0.16 43.69)', // orange.70 (darkened from orange.50)
  riskLow: 'oklch(52% 0.14 81.73)', // yellow.70 (darkened from yellow.50 — yellow gamut issue)
  riskSafe: 'oklch(50% 0.14 145.76)', // green.70 (darkened from green.50)
  riskInfo: 'oklch(50% 0.16 262.58)', // blue.70 (darkened from blue.50)
  riskUnknown: 'oklch(49.12% 0.0487 268.44)', // bice.50 — already passes
  warnBadgeText: 'oklch(85% 0.12 85)',
  warnBadgeSurface: 'oklch(85% 0.12 85 / 0.12)',
  warnBadgeBorder: 'oklch(85% 0.12 85 / 0.28)',
  motionFast: '120ms',
  motionBase: '200ms',
  motionSlow: '320ms',
  easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
  easeInOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  fontBody:
    '"Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontDisplay:
    'Interstate-Light, "Helvetica Neue", Helvetica, Arial, sans-serif',
  fontMono:
    '"Segoe UI Mono", "SFMono-Medium", "SF Mono", Menlo, Consolas, Courier, monospace',
  shadowThin: '0 2px 8px 0 rgba(7,9,13,.48), 0 1px 2px 0 rgba(7,9,13,.16)',
  shadowMedium: '0 4px 16px 0 rgba(0,0,0,.48), 0 2px 4px 0 rgba(0,0,0,.16)',
  shadowThick: '0 8px 32px 0 rgba(0,0,0,.48), 0 4px 8px 0 rgba(0,0,0,.16)',
  shadowGlow: '0 0 0 1px oklch(47.34% 0.1717 353.56 / 0.32)',
});
